import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as core from '../../../core'

import { Mapping } from './mapping'
import { Filter } from './request'

type RuleBase = core.common.Identified<Rule.Id> &
  core.common.Named &
  core.common.Prioritized &
  core.common.Activateable
export interface Rule extends RuleBase {
  filter: Filter
  mapping: Mapping
}
export namespace Rule {
  const FIELD_CHECKERS: TypeChecker.Factory.object.Fields<Rule> = {
    filter: TypeChecker.Factory.lazy(() => Filter.isFilter),
    id: TypeChecker.Factory.lazy(() => Id.isId),
    isActive: TypeChecker.isBoolean,
    mapping: TypeChecker.Factory.lazy(() => Mapping.isMapping),
    name: TypeChecker.isString,
    priority: TypeChecker.isNumber,
  }

  export const isRule = TypeChecker.Factory.object<Rule>(FIELD_CHECKERS)

  export type Id = number
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.Factory.custom(
      (val: any): val is Id => TypeChecker.isNumber(val) && !isNaN(val),
      { typeDescription: 'Rule.Id' },
    )
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  const INPUT_FIELD_CHECKERS: TypeChecker.Factory.object.Fields<Input> = {
    ...FIELD_CHECKERS,
    isActive: TypeChecker.Factory.optional(FIELD_CHECKERS.isActive),
  }
  delete (INPUT_FIELD_CHECKERS as Partial<typeof FIELD_CHECKERS>).id

  export type Input = Partial.Semi<Omit<Rule, 'id'>, 'isActive'>
  export namespace Input {
    export const isInput =
      TypeChecker.Factory.object<Input>(INPUT_FIELD_CHECKERS)

    /**
     * Creates a {@link Rule.Input} with some initial default values
     */
    export function generate({
      filter = {
        connector: Filter.Statement.Connector.And,
        filters: [],
      },
      isActive = true,
      mapping = {
        entries: [],
      },
      name = '',
      priority = 1,
    }: Partial<Input> = {}): Input {
      return {
        filter,
        isActive,
        mapping,
        name,
        priority,
      }
    }
  }

  export type Updates = Partial<Input>
  export namespace Updates {
    export const isUpdates =
      TypeChecker.Factory.object.partial<Updates>(INPUT_FIELD_CHECKERS)
  }
}
